// ============================================================================
// PatientCabinetPage — «МОЙ КАБИНЕТ» пациента «ГидраМедик».
//
// Единый экран пациента (SSoT данных — lib/patientCabinetDashboard, SSoT прав —
// сервер): свои тесты/результаты, записи и приёмы, исследования ЭКГ, доступ и
// приглашения. Вкладки:
//   • «Тесты» — назначения психолога (ссылка/код, результаты) либо общий кабинет
//     опросов (без модуля psy);
//   • «Записи» — приёмы medic_encounters и запись к специалисту psy_bookings;
//   • «ЭКГ» — история cardio_studies (ТОЛЬКО просмотр);
//   • «Доступ» — связь учётной записи с карточками, доступность разделов и
//     выданные приглашения на тестирование.
//
// Права роли «Пациент» решает СЕРВЕР: закрытый раздел показывается явным
// ограничением (denied), а не пустым списком. Медицинские данные кабинет НЕ
// изменяет; единственное действие — прохождение назначенного теста.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HeartPulse, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TabBar } from '@/components/ui/TabBar';
import { SectionState } from '@/components/medic/PatientCardStates';
import { PatientTestsTab } from '@/components/cabinet/patient/PatientTestsTab';
import { PatientVisitsTab } from '@/components/cabinet/patient/PatientVisitsTab';
import { PatientEcgTab } from '@/components/cabinet/patient/PatientEcgTab';
import { PatientAccessTab } from '@/components/cabinet/patient/PatientAccessTab';
import { MedicAppointmentsPage } from '@/components/medic/appointments/MedicAppointmentsPage';
import { useAuthStore } from '@/store/authStore';
import { logErrorToJournal, friendlyError } from '@/lib/errors';
import { loadPatientCabinetDashboard, type PatientCabinetDashboard } from '@/lib/patientCabinetDashboard';

type CabinetTab = 'tests' | 'visits' | 'book' | 'ecg' | 'access';

function testsCount(data: PatientCabinetDashboard): number {
    if (data.tests.kind === 'psy') return data.tests.assignments.length + data.tests.results.length;
    if (data.tests.kind === 'poll' && data.tests.poll) {
        return data.tests.poll.tests.length + data.tests.poll.protocols.length;
    }
    return 0;
}

function visitsCount(data: PatientCabinetDashboard): number {
    return data.encounters.items.length + data.bookings.items.length;
}

export function PatientCabinetPage() {
    const user = useAuthStore((state) => state.user);
    const userId = String(user?.id || '');
    const userName = String(user?.name || '');
    const userLogin = String(user?.login || '');
    const userPhone = String(user?.phone || '');
    const userEmail = String(user?.email || '');
    const userRoles = Array.isArray(user?.roles) ? user.roles.join('|') : '';
    const userPermissions = Array.isArray(user?.permissions) ? user.permissions.join('|') : '';

    const [data, setData] = useState<PatientCabinetDashboard | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [tab, setTab] = useState<CabinetTab>('tests');
    // Номер запроса: ответ устаревшей загрузки не перетирает свежий.
    const requestRef = useRef(0);
    // Для какой учётной записи показан текущий снимок. Смена пользователя
    // СБРАСЫВАЕТ данные — иначе до ответа сервера на экране остались бы
    // медицинские данные предыдущего человека.
    const snapshotUserIdRef = useRef('');

    const load = useCallback(async () => {
        if (!userId) {
            requestRef.current += 1;
            snapshotUserIdRef.current = '';
            setData(null);
            setError('Не определён текущий пользователь — войдите заново');
            setLoading(false);
            return;
        }
        if (snapshotUserIdRef.current !== userId) {
            snapshotUserIdRef.current = userId;
            setData(null);
            setError('');
            setTab('tests');
        }
        const request = ++requestRef.current;
        setLoading(true);
        try {
            const snapshot = await loadPatientCabinetDashboard({
                id: userId,
                name: userName,
                login: userLogin,
                phone: userPhone,
                email: userEmail,
                roles: userRoles ? userRoles.split('|') : [],
                permissions: userPermissions ? userPermissions.split('|') : [],
            });
            if (requestRef.current !== request) return;
            setData(snapshot);
            setError('');
        } catch (e) {
            if (requestRef.current !== request) return;
            logErrorToJournal(e, 'Кабинет пациента: загрузка');
            setError(friendlyError(e));
        } finally {
            if (requestRef.current === request) setLoading(false);
        }
    }, [userId, userName, userLogin, userPhone, userEmail, userRoles, userPermissions]);

    useEffect(() => () => {
        // Размонтирование инвалидирует незавершённую загрузку.
        requestRef.current += 1;
    }, []);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            // Микрозадача: эффект не вызывает setState синхронно (React 19).
            await Promise.resolve();
            if (!cancelled) await load();
        };
        void run();
        return () => { cancelled = true; };
    }, [load]);

    const refresh = useCallback(() => { void load(); }, [load]);

    const patientName = useMemo(() => {
        if (!data) return '';
        return data.identity.psy?.name
            || data.identity.medic?.name
            || data.identity.name
            || data.identity.login
            || '';
    }, [data]);

    const tabs = useMemo(() => {
        if (!data) return [];
        return [
            { id: 'tests', label: `Тесты (${testsCount(data)})` },
            { id: 'visits', label: `Записи (${visitsCount(data)})` },
            { id: 'book', label: 'Запись' },
            { id: 'ecg', label: `ЭКГ (${data.ecg.studies.length})` },
            { id: 'access', label: 'Доступ' },
        ];
    }, [data]);

    if (!userId) {
        return (
            <SectionState
                title="Кабинет недоступен"
                text="Не определён текущий пользователь — войдите заново."
                tone="warning"
            />
        );
    }

    // Снимок показывается ТОЛЬКО для текущей учётной записи: при смене
    // пользователя старые медицинские данные не остаются на экране ни кадра
    // (сброс в load() происходит уже после рендера).
    if (!data || data.identity.userId !== userId) {
        if (error) {
            return (
                <SectionState
                    title="Не удалось загрузить кабинет"
                    text={error}
                    tone="danger"
                    onRetry={refresh}
                />
            );
        }
        return (
            <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />Загрузка кабинета…
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="flex shrink-0 items-center gap-2 border-b px-4 py-2.5">
                <HeartPulse className="h-4 w-4 shrink-0 text-primary" />
                <span className="shrink-0 text-sm font-medium">Мой кабинет</span>
                {patientName && (
                    <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground" title={patientName}>
                        {patientName}
                    </span>
                )}
                {data.identity.isAdmin && (
                    <span className="shrink-0 rounded-full border bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
                        администратор
                    </span>
                )}
                <Button
                    size="sm"
                    variant="outline"
                    className="ml-auto h-7 shrink-0 px-2"
                    onClick={refresh}
                    disabled={loading}
                    title="Обновить"
                >
                    {loading
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <RefreshCw className="h-3.5 w-3.5" />}
                </Button>
            </div>

            <div className="shrink-0 border-b px-3 py-2">
                <TabBar
                    tabs={tabs}
                    active={tab}
                    onChange={(id) => setTab(id as CabinetTab)}
                    group="patient-cabinet"
                />
            </div>

            <div className={tab === 'book' ? 'min-h-0 flex-1 overflow-hidden' : 'min-h-0 flex-1 overflow-auto p-4'}>
                {error && (
                    <div className="mb-3">
                        <SectionState
                            title="Данные могли не обновиться"
                            text={error}
                            tone="danger"
                            onRetry={refresh}
                        />
                    </div>
                )}
                {tab === 'tests' && <PatientTestsTab data={data.tests} onRefresh={refresh} />}
                {tab === 'visits' && (
                    <PatientVisitsTab
                        encounters={data.encounters}
                        bookings={data.bookings}
                        onRefresh={refresh}
                    />
                )}
                {tab === 'book' && <MedicAppointmentsPage embedded forceMode="patient" />}
                {tab === 'ecg' && <PatientEcgTab data={data.ecg} onRefresh={refresh} />}
                {tab === 'access' && <PatientAccessTab data={data} onRefresh={refresh} />}
            </div>
        </div>
    );
}
